import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import Button from './Button';

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { connected } = useSocket();
  const [isCollapsed, setIsCollapsed] = useState(true); // Start minimized by default
  const [isHovered, setIsHovered] = useState(false);

  // Simplified menu - all navigation happens through dashboard tabs
  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', icon: 'home' },
    { path: '/profile', label: 'Profile', icon: 'profile' },
    // Admin menu items - only visible to system_admin
    ...(user?.role === 'system_admin' ? [
      { path: '/admin/data-editor', label: 'Data Editor', icon: 'database' },
      { path: '/admin/import', label: 'Import Data', icon: 'upload' }
    ] : [])
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Icon components
  const Icon = ({ type, className }) => {
    const icons = {
      home: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      projects: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      users: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      leads: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      distribute: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      measurements: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      menu: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
      profile: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      database: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      upload: (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      )
    };
    return icons[type] || null;
  };

  // Determine if sidebar should be expanded
  const isExpanded = !isCollapsed || isHovered;

  return (
    <>
      {/* Mobile Menu Toggle - only show when sidebar is collapsed */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="md:hidden fixed top-4 left-4 z-50 p-2.5 bg-tenq-black text-white rounded-lg shadow-lg"
        >
          <Icon type="menu" className="w-6 h-6" />
        </button>
      )}

      {/* Sidebar */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`fixed left-0 top-0 h-full bg-tenq-black text-white z-40 transition-all duration-300 flex flex-col ${
          isCollapsed ? '-translate-x-full md:translate-x-0' : ''
        } ${isExpanded ? 'w-64' : 'md:w-20 w-64'}`}
      >
        {/* Logo */}
        <div className={`border-b border-white/10 transition-all duration-300 ${isExpanded ? 'p-4' : 'py-4 px-2'}`}>
          <div className={`flex items-center ${isExpanded ? 'justify-between' : 'justify-center'}`}>
            <div className={`flex items-center overflow-hidden ${isExpanded ? '' : 'justify-center'}`}>
              <img
                src="/logo.png"
                alt="TenQ CRM"
                className={`flex-shrink-0 transition-all duration-300 ${isExpanded ? 'h-10' : 'h-12'}`}
              />
              <div
                className={`overflow-hidden transition-all duration-300 whitespace-nowrap ${
                  isExpanded ? 'w-32 opacity-100 ml-3' : 'w-0 opacity-0 ml-0'
                }`}
              >
                <h1 className="text-xl font-display font-bold text-tenq-gold">TenQ CRM</h1>
                <p className="text-xs text-white/60">Interior Design</p>
              </div>
            </div>
            {isExpanded && (
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden md:block p-1 rounded hover:bg-white/10 flex-shrink-0"
              >
                <svg className="w-5 h-5 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (window.innerWidth < 1024) setIsCollapsed(true);
              }}
              className={`w-full flex items-center gap-3 rounded-lg transition-colors ${
                isExpanded ? 'px-4 py-3' : 'px-3 py-3 justify-center'
              } ${
                isActive(item.path)
                  ? 'bg-tenq-gold text-tenq-black font-semibold'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
              title={!isExpanded ? item.label : ''}
            >
              <Icon type={item.icon} className="w-5 h-5 flex-shrink-0" />
              {isExpanded && <span className="whitespace-nowrap">{item.label}</span>}
            </button>
          ))}
        </nav>

        {/* Bottom Section - User Info & Status */}
        <div className="p-4 border-t border-white/10 space-y-4">
          {/* Socket Status */}
          {isExpanded ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg">
              <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="text-xs text-white/70">
                {connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          ) : (
            <div className="flex justify-center py-2">
              <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} title={connected ? 'Connected' : 'Disconnected'}></div>
            </div>
          )}

          {/* User Info */}
          {isExpanded ? (
            <div className="px-4 py-2 bg-white/5 rounded-lg">
              <p className="text-sm font-medium text-white">{user?.fullName}</p>
              <p className="text-xs text-white/60">{user?.nickname} • {user?.role}</p>
            </div>
          ) : (
            <div className="flex justify-center py-2 bg-white/5 rounded-lg" title={user?.fullName}>
              <span className="text-sm font-bold text-tenq-gold">
                {user?.nickname?.charAt(0)?.toUpperCase() || user?.fullName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
          )}

          {/* Logout Button */}
          {isExpanded ? (
            <Button
              variant="outline-gold"
              size="sm"
              className="w-full"
              onClick={handleLogout}
            >
              Logout
            </Button>
          ) : (
            <button
              onClick={handleLogout}
              className="w-full py-2 rounded-lg border border-tenq-gold text-tenq-gold hover:bg-tenq-gold hover:text-tenq-black transition-colors flex items-center justify-center"
              title="Logout"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Overlay for mobile */}
      {!isCollapsed && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setIsCollapsed(true)}
        />
      )}
    </>
  );
}

export default Sidebar;
